import { PureComponent } from 'react';
import SnowflakeTooltip from './SnowflakeTooltip';
import { CopyBlock, ScoreLabel } from './SnowflakeTooltip/styled';
import Slider from './Slider';
import QuestionMarkIconSVG from '@assets/images/icons/legacy/question-circle.svg';
import { MediaWrapper, SnowflakeWrapper } from './styled';
import { SWS_HOST } from '@/constants';
import { withTheme } from 'styled-components';
import type { WithThemeProps } from '@simplywallst/ui-core';
import type { AnalysisChecks } from '@/pages/CompanyReport/components/SummaryCompare';
import { SnowflakeType } from './SnowflakeType';
import type { SnowflakeSection } from './SnowflakeCanvas';
import If from '@components/If';
import SnowflakeCanvas from './SnowflakeCanvas';

export interface SnowflakeProps {
  type?: SnowflakeType;
  anchors?: number[];
  points?: number[];
  routes?: string[];
  width?: number;
  dark?: boolean;
  light?: boolean;
  editable?: boolean;
  disabled?: boolean;
  tooltip?: boolean;
  description?: string;
  disableLabel?: boolean;
  currentHoldingCount?: number;
  detailedPoints?: AnalysisChecks;
  highlightSection?: SnowflakeSection;
  handleClick?: (route: string) => void;
  handleChange?: (points: number[]) => void;
  companyDescriptions?: string[];
}

interface State {
  selectedIndex: number;
}

const defaultDescriptions = [
  'Is the company undervalued compared to its peers, industry and forecasted cash flows?',
  'How is the company forecast to perform in the next 1-3 years?',
  'How has the company performed over the past 5 years?',
  'Does the company have strong financial health and manageable debt?',
  'Does the company pay a good, reliable and sustainable dividend?',
];

const companyReportAnchors = ['1', '2', '3', '4', '5'];
const portfolioAnchors = ['6', '7', '8', '9', '10'];
class Snowflake extends PureComponent<SnowflakeProps & WithThemeProps, State> {
  static defaultProps: SnowflakeProps = {
    dark: false,
    light: false,
    routes: [],
    editable: false,
    disabled: false,
    description: '',
    disableLabel: false,
    tooltip: false,
    points: [1, 1, 1, 1, 1],
    handleClick: () => true,
    handleChange: () => null,
    companyDescriptions: defaultDescriptions,
  };

  state = {
    selectedIndex: -1,
  };

  handleClick = (i) => {
    const { handleClick, routes, theme } = this.props;
    const screenWidth =
      RUNTIME_ENV !== 'server'
        ? window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth
        : theme.lgMinRaw;
    if (routes && routes.length && screenWidth >= theme.lgMinRaw) {
      handleClick && handleClick(routes[i]);
    }
  };

  render() {
    const { handleClick } = this;

    const { handleChange } = this.props;

    const { selectedIndex } = this.state;

    const {
      dark,
      light,
      width,
      points,
      detailedPoints,
      disabled,
      disableLabel,
      tooltip,
      editable,
      description,
      type,
      highlightSection,
      currentHoldingCount,
      companyDescriptions = defaultDescriptions,
    } = this.props;
    const enableTooltip = tooltip && !editable && !disabled;

    let pointsMinusOne: number[] = [1, 1, 1, 1, 1];
    try {
      // sometimes `points` is `null`
      pointsMinusOne = (points as number[]).map((point) => point - 1);
    } catch (e) {
      // noop
    }
    const portfolioDescriptions = [
      `This portfolios average Value score from ${currentHoldingCount} holdings`,
      `This portfolios average Future Growth score from ${currentHoldingCount} holdings`,
      `This portfolios average Past Performance score from ${currentHoldingCount} holdings`,
      `This portfolios average Financial Health score from ${currentHoldingCount} holdings`,
      `This portfolios average Dividend score from ${currentHoldingCount} holdings`,
    ];

    return (
      <SnowflakeWrapper role="figure" aria-label="Snowflake chart">
        <MediaWrapper>
          <SnowflakeCanvas
            dark={dark}
            size={width}
            light={light}
            points={points || []}
            disabled={disabled}
            disableLabel={disableLabel}
            highlightSection={highlightSection}
          />

          <If
            conditions={{
              showCompanyTooltips:
                enableTooltip === true && type === SnowflakeType.COMPANY,
              showTOCSnowflake:
                enableTooltip === true && type === SnowflakeType.TOC,
              showPortfolioTooltips:
                enableTooltip === true &&
                (type === SnowflakeType.PORTFOLIO ||
                  type === SnowflakeType.WATCHLIST),
            }}
          >
            {{
              showCompanyTooltips: () => (
                <SnowflakeTooltip
                  points={pointsMinusOne}
                  detailedPoints={detailedPoints}
                  anchors={companyReportAnchors}
                  onClick={handleClick}
                  descriptions={companyDescriptions}
                  selectorPrefix="summary"
                />
              ),
              showTOCSnowflake: () => (
                <SnowflakeTooltip
                  points={pointsMinusOne}
                  anchors={companyReportAnchors}
                  onClick={handleClick}
                  hasHoverInfo={false}
                  descriptions={companyDescriptions}
                  selectorPrefix="side-nav"
                />
              ),
              showPortfolioTooltips: () => (
                <SnowflakeTooltip
                  points={points as number[]}
                  anchors={portfolioAnchors}
                  onClick={handleClick}
                  descriptions={portfolioDescriptions}
                >
                  {(score) => (
                    <CopyBlock>
                      Score <ScoreLabel>{(score - 1).toFixed(2)}/6</ScoreLabel>
                    </CopyBlock>
                  )}
                </SnowflakeTooltip>
              ),
            }}
          </If>
          {editable && !tooltip && !disabled && (
            <Slider
              points={points as number[]}
              selectedIndex={selectedIndex}
              onChange={handleChange}
              dimension={width}
            />
          )}
        </MediaWrapper>
        {description && (
          <span>
            <h4>Snowflake Description</h4>
            <p>
              <span>{`${description} `}</span>
              <a href={`${SWS_HOST}/learning-center/snowflake-introduction`}>
                <QuestionMarkIconSVG />
              </a>
            </p>
          </span>
        )}
      </SnowflakeWrapper>
    );
  }
}

export default withTheme(Snowflake);
